import React, { useState, useRef, useEffect } from 'react';
import './ChatbotSidebar.css';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { getApiUrl } from '../utils/api';
import { auth } from '../firebase';

function fixMarkdownTables(text: string): string {
  // Remove <p> tags that wrap table lines
  return text.replace(/<p>([\s\S]*?)<\/p>/g, (match, inner) => {
    if (inner.trim().startsWith('|')) {
      return inner.trim();
    }
    return match;
  });
}

const SUGGESTIONS = [
  'Show me a table of all blocks',
  'What are the video settings?',
  'How many cast members are there?',
];

interface Edit {
  row: number;
  col: number;
  value: string;
}

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
  pendingEdits?: Edit[];
  confirmed?: boolean;
  snapshot?: Edit[];   // stores original values for undo
  isRetryable?: boolean;
}

interface ChatbotSidebarProps {
  sheetsUrl?: string | null;
  onLoadGoogleSheets?: () => void;
}

const ALLOWED_CHATBOT_EMAILS = new Set([
  'daniel.udassin@gmail.com',
  'eyal.udassin@gmail.com',
  "elad@markerfilms.com",
]);

const ChatbotSidebar: React.FC<ChatbotSidebarProps> = ({ sheetsUrl, onLoadGoogleSheets }) => {
  const userEmail = auth.currentUser?.email?.toLowerCase() ?? '';
  const [isOpen, setIsOpen] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: 'Production Assistant online. Ready to help with your project. What scene are we working on today?',
      sender: 'bot',
      timestamp: new Date(),
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const resizeInput = () => {
    const input = inputRef.current;
    if (!input) return;
    
    const MAX_INPUT_HEIGHT = 120;
    input.style.height = 'auto';
    const nextHeight = Math.min(input.scrollHeight, MAX_INPUT_HEIGHT);
    input.style.height = `${nextHeight}px`;
    input.style.overflowY = input.scrollHeight > MAX_INPUT_HEIGHT ? 'auto' : 'hidden';
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    resizeInput();
  }, [inputValue, isOpen]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
      resizeInput();
    }
  }, [isOpen]);

  // ============================================
  // CORE SEND LOGIC — shared by handleSendMessage,
  // handleSuggestionClick, and handleRetry
  // ============================================
  const sendMessage = async (text: string, history: { role: string; content: string }[]) => {
    setIsTyping(true);

    try {
      const payload = { input: text, sheetUrl: sheetsUrl ?? '', history };

      const response = await fetch(`${getApiUrl()}/chatbot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (result.status === 'PENDING') {
        // Edit preview — show confirmation bubble
        setMessages((prev) => [...prev, {
          id: (Date.now() + 1).toString(),
          text: result.message,
          sender: 'bot',
          timestamp: new Date(),
          pendingEdits: result.edits,
        }]);

      } else if (result.status === 'OK') {
        let botText: string;
        if (Array.isArray(result.message)) {
          botText = result.message
            .map((edit: Edit) => `Row ${edit.row}, Col ${edit.col}: ${edit.value}`)
            .join('\n');
        } else if (typeof result.message === 'string') {
          botText = fixMarkdownTables(result.message.replace(/\\n/g, '\n'));
        } else {
          botText = String(result.message);
        }
        setMessages((prev) => [...prev, {
          id: (Date.now() + 1).toString(),
          text: botText,
          sender: 'bot',
          timestamp: new Date(),
        }]);

      } else if (result.status === 'ERROR') {
        const errorText = `**${result.friendly}**\n\n\`\`\`\n${result.detail}\n\`\`\``;
        setMessages((prev) => [...prev, {
          id: (Date.now() + 1).toString(),
          text: errorText,
          sender: 'bot',
          timestamp: new Date(),
          isRetryable: true,
        }]);

      } else {
        // Fallback for unexpected statuses
        setMessages((prev) => [...prev, {
          id: (Date.now() + 1).toString(),
          text: 'Unexpected response from server.',
          sender: 'bot',
          timestamp: new Date(),
          isRetryable: true,
        }]);
      }
    } catch (err) {
      const detail = err instanceof Error ? err.message : String(err);
      setMessages((prev) => [...prev, {
        id: (Date.now() + 1).toString(),
        text: `**Network error. Check your connection and try again.**\n\n\`\`\`\n${detail}\n\`\`\``,
        sender: 'bot',
        timestamp: new Date(),
        isRetryable: true,
      }]);
    } finally {
      setIsTyping(false);
    }
  };

  // ============================================
  // SEND MESSAGE — triggered by input
  // ============================================
  const handleSendMessage = async () => {
    const trimmedInput = inputValue.trim();
    if (!trimmedInput) return;

    setHasInteracted(true);
    setMessages((prev) => [...prev, {
      id: Date.now().toString(),
      text: trimmedInput,
      sender: 'user',
      timestamp: new Date(),
    }]);
    setInputValue('');

    const history = messages.map((m) => ({
      role: m.sender === 'user' ? 'user' : 'assistant',
      content: m.text,
    }));

    await sendMessage(trimmedInput, history);
  };

  // ============================================
  // SUGGESTION CLICK — triggered by suggestion chips
  // ============================================
  const handleSuggestionClick = async (suggestion: string) => {
    setHasInteracted(true);
    setMessages((prev) => [...prev, {
      id: Date.now().toString(),
      text: suggestion,
      sender: 'user',
      timestamp: new Date(),
    }]);

    await sendMessage(suggestion, []);
  };

  // ============================================
  // RETRY — resends last user message after error
  // ============================================
  const handleRetry = async () => {
    const lastUserMessage = [...messages].reverse().find((m) => m.sender === 'user');
    if (!lastUserMessage) return;

    // Remove all retryable error messages
    setMessages((prev) => prev.filter((m) => !m.isRetryable));

    const history = messages
      .filter((m) => !m.isRetryable)
      .map((m) => ({
        role: m.sender === 'user' ? 'user' : 'assistant',
        content: m.text,
      }));

    await sendMessage(lastUserMessage.text, history);
  };

  // ============================================
  // CONFIRM EDIT — applies pending edits to sheet
  // ============================================
  const handleConfirmEdit = async (messageId: string, edits: Edit[]) => {
    // Mark the message as confirmed
    setMessages((prev) =>
      prev.map((m) => (m.id === messageId ? { ...m, confirmed: true } : m))
    );
    setIsTyping(true);

    try {
      const response = await fetch(`${getApiUrl()}/chatbot/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sheetUrl: sheetsUrl ?? '', edits }),
      });

      const result = await response.json();

      const text = result.status === 'OK'
        ? result.message
        : `**${result.friendly}**\n\n\`\`\`\n${result.detail}\n\`\`\``;

      setMessages((prev) => [...prev, {
        id: (Date.now() + 1).toString(),
        text,
        sender: 'bot',
        timestamp: new Date(),
        snapshot: result.status === 'OK' ? result.snapshot : undefined,
      }]);
      if (result.status === 'OK') {
        onLoadGoogleSheets?.();
      }
    } catch (err) {
      const detail = err instanceof Error ? err.message : String(err);
      setMessages((prev) => [...prev, {
        id: (Date.now() + 1).toString(),
        text: `**Failed to apply edits. Check your connection and try again.**\n\n\`\`\`\n${detail}\n\`\`\``,
        sender: 'bot',
        timestamp: new Date(),
      }]);
    } finally {
      setIsTyping(false);
    }
  };

  // ============================================
  // CANCEL EDIT — discards pending edits
  // ============================================
  const handleCancelEdit = (messageId: string) => {
    // Mark as cancelled and add a cancel message
    setMessages((prev) =>
      prev.map((m) => (m.id === messageId ? { ...m, confirmed: false } : m))
    );
    setMessages((prev) => [...prev, {
      id: (Date.now() + 1).toString(),
      text: 'Edit cancelled. No changes were made.',
      sender: 'bot',
      timestamp: new Date(),
    }]);
  };

  const handleScroll = () => {
  const container = messagesContainerRef.current;
  if (!container) return;
  const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
  setShowScrollButton(distanceFromBottom > 100);
};

  // ============================================
  // UNDO EDIT — restores cells to original values
  // ============================================
  const handleUndo = async (messageId: string, snapshot: Edit[]) => {
    // Remove the undo button from this message
    setMessages((prev) =>
      prev.map((m) => (m.id === messageId ? { ...m, snapshot: undefined } : m))
    );
    setIsTyping(true);

    try {
      const response = await fetch(`${getApiUrl()}/chatbot/undo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sheetUrl: sheetsUrl ?? '', snapshot }),
      });

      const result = await response.json();
      const text = result.status === 'OK'
        ? result.message
        : `**${result.friendly}**\n\n\`\`\`\n${result.detail}\n\`\`\``;

      setMessages((prev) => [...prev, {
        id: (Date.now() + 1).toString(),
        text,
        sender: 'bot',
        timestamp: new Date(),
      }]);
      if (result.status === 'OK') {
        onLoadGoogleSheets?.();
      }
    } catch (err) {
      const detail = err instanceof Error ? err.message : String(err);
      setMessages((prev) => [...prev, {
        id: (Date.now() + 1).toString(),
        text: `**Failed to undo. Check your connection and try again.**\n\n\`\`\`\n${detail}\n\`\`\``,
        sender: 'bot',
        timestamp: new Date(),
      }]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleClearChat = () => {
    setMessages([{
      id: Date.now().toString(),
      text: 'Production Assistant online. Ready to help with your project. What scene are we working on today?',
      sender: 'bot',
      timestamp: new Date(),
    }]);
    setHasInteracted(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (!ALLOWED_CHATBOT_EMAILS.has(userEmail)) {
    return null;
  }

  return (
    <>
      {/* Toggle Button */}
      <button
        className={`chatbot-toggle ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Toggle chatbot"
      >
        <svg className="icon-chat" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
        <svg className="icon-close" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>

      {/* Sidebar */}
      <div className={`chatbot-sidebar ${isOpen ? 'open' : ''}`}>
        <div className="chatbot-header">
          <div className="header-content">
            <button className="clear-btn" onClick={handleClearChat} aria-label="Clear chat">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6l-1 14H6L5 6" />
                <path d="M10 11v6M14 11v6" />
                <path d="M9 6V4h6v2" />
              </svg>
            </button>
            <div className="logo-section">
              <div className="terminal-icon">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z"/>
                </svg>
              </div>
              <div className="header-text">
                <h3>Production Hub</h3>
                <div className="status-line">
                  <span className="status-dot"></span>
                  <span className="status-text">In Production</span>
                </div>
              </div>
            </div>
            <button className="minimize-btn" onClick={() => setIsOpen(false)} aria-label="Close chatbot">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </button>
          </div>
          <div className="scan-line"></div>
        </div>

        {/* Messages */}
        <div className="chatbot-messages" ref={messagesContainerRef} onScroll={handleScroll}>
          {messages.map((message) => (
            <div key={message.id} className={`message ${message.sender}`}>
              <div className="message-avatar">
                {message.sender === 'bot' ? (
                  <svg viewBox="0 0 28 28" fill="currentColor">
                    <path d="m 21.896484,5.0566406 -0.875,1.7832032 c 1.606692,0.78762 2.911492,2.1908405 3.539063,4.0253902 0.62757,1.83455 0.455603,3.744873 -0.332031,5.351563 l 1.783203,0.873047 
                      C 27.026493,15.019794 27.255618,12.56465 26.453125,10.21875 25.65063,7.8728599 23.966527,6.0714206 21.896484,5.0566406 Z M 16.833984,5.9257812 3.2734375,15.6875 c -0.2145341,1.245805 0.048882,2.39094 0.6386719,3.466797 l 3.4394531,-0.02734 1.484375,5.433594 4.0820315,0.0059 -1.414063,-5.439453 
                      9.28125,-0.414062 C 20.21855,15.356069 19.152501,12.26109 16.833984,5.9257812 Z M 20.576172,7.75 19.699219,9.5351562 c 0.917304,0.4496801 1.664005,1.2500708 2.023437,
                      2.3007818 0.359434,1.05071 0.260258,2.141303 -0.189453,3.058593 l 1.785156,0.875 c 0.676826,-1.38065 0.829275,-3.018018 0.294922,-4.580078 C 23.078924,9.627393 21.956828,
                      8.42683 20.576172,7.75 Z" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                  </svg>
                )}
              </div>

              <div className="message-content">
                <div className={`message-bubble ${message.pendingEdits ? 'pending' : ''}`}>
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeRaw]}
                    remarkRehypeOptions={{ allowDangerousHtml: true }}
                  >
                    {message.text}
                  </ReactMarkdown>

                  {/* Pending edits table */}
                  {message.pendingEdits && message.confirmed === undefined && (
                    <div className="edit-preview">
                      <div className="edit-preview-table-wrapper">
                        <table className="edit-preview-table">
                          <thead>
                            <tr>
                              <th>Row</th>
                              <th>Col</th>
                              <th>New Value</th>
                            </tr>
                          </thead>
                          <tbody>
                            {message.pendingEdits.map((edit, i) => (
                              <tr key={i}>
                                <td>{edit.row}</td>
                                <td>{edit.col}</td>
                                <td>{edit.value}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="edit-confirm-buttons">
                        <button
                          className="confirm-btn"
                          onClick={() => handleConfirmEdit(message.id, message.pendingEdits!)}
                        >
                          ✓ Confirm
                        </button>
                        <button
                          className="cancel-btn"
                          onClick={() => handleCancelEdit(message.id)}
                        >
                          ✗ Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Resolved state */}
                  {message.pendingEdits && message.confirmed === true && (
                    <div className="edit-resolved confirmed">✓ Applied</div>
                  )}
                  {message.pendingEdits && message.confirmed === false && (
                    <div className="edit-resolved cancelled">✗ Cancelled</div>
                  )}

                  {/* Undo button — shown after a successful edit apply */}
                  {message.snapshot && (
                    <button
                      className="undo-btn"
                      onClick={() => handleUndo(message.id, message.snapshot!)}
                    >
                      ↩ Undo
                    </button>
                  )}

                  {/* Retry button — shown on error messages */}
                  {message.isRetryable && (
                    <button className="retry-btn" onClick={handleRetry}>
                      ↺ Retry
                    </button>
                  )}
                </div>

                <div className="message-time">
                  {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            </div>
          ))}

          {/* Typing indicator */}
          {isTyping && (
            <div className="message bot typing">
              <div className="message-avatar">
                <svg viewBox="0 0 28 28" fill="currentColor">
                  <path d="m 21.896484,5.0566406 -0.875,1.7832032 c 1.606692,0.78762 2.911492,2.1908405 3.539063,4.0253902 0.62757,1.83455 0.455603,3.744873 -0.332031,5.351563 l 1.783203,0.873047 
                      C 27.026493,15.019794 27.255618,12.56465 26.453125,10.21875 25.65063,7.8728599 23.966527,6.0714206 21.896484,5.0566406 Z M 16.833984,5.9257812 3.2734375,15.6875 c -0.2145341,1.245805 0.048882,2.39094 0.6386719,3.466797 l 3.4394531,-0.02734 1.484375,5.433594 4.0820315,0.0059 -1.414063,-5.439453 
                      9.28125,-0.414062 C 20.21855,15.356069 19.152501,12.26109 16.833984,5.9257812 Z M 20.576172,7.75 19.699219,9.5351562 c 0.917304,0.4496801 1.664005,1.2500708 2.023437,
                      2.3007818 0.359434,1.05071 0.260258,2.141303 -0.189453,3.058593 l 1.785156,0.875 c 0.676826,-1.38065 0.829275,-3.018018 0.294922,-4.580078 C 23.078924,9.627393 21.956828,
                      8.42683 20.576172,7.75 Z" />
                </svg>
              </div>
              <div className="message-content">
                <div className="message-bubble">
                  <span className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
          {showScrollButton && (
            <button
              className="scroll-bottom-btn"
              onClick={scrollToBottom}
              aria-label="Scroll to bottom"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
          )}
        </div>

        {/* Suggestion chips — only before first interaction */}
        {!hasInteracted && (
          <div className="suggestions">
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                className="suggestion-chip"
                onClick={() => handleSuggestionClick(s)}
              >
                {s}
              </button>
            ))}
          </div>
        )}

        {/* Input area */}
        <div className="chatbot-input">
          <div className="chatbot-input-wrapper">
            <textarea
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Ask about your production..."
              className="message-input"
              rows={1}
            />
            <button
              className="send-button"
              onClick={handleSendMessage}
              disabled={!inputValue.trim()}
              aria-label="Send message"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </button>
          </div>
          <div className="terminal-footer">
            <span className="footer-text">Enter to send | Shift+Enter for newline</span>
          </div>
        </div>
      </div>

      {/* Backdrop */}
      {isOpen && <div className="chatbot-backdrop" onClick={() => setIsOpen(false)} />}
    </>
  );
};

export default ChatbotSidebar;
